import { Divider, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';
import { useShallow } from 'zustand/react/shallow';

import { Caution, ModalContentsContainer } from '~/components/common';

import { useGetOrders } from '~/hooks/order';
import { initialClaimStoreValue, useClaimStore } from '~/store';
import { addIf, formatDate, validateAlphabetNumericOnly } from '~/utils';

import { INITIAL_CLAIM_PAGE_CURSOR, MAX_CLAIM_DATE_RANGE } from '~/constants/claim';
import { OPTION_ALL, TOAST_MESSAGE } from '~/constants/common';
import { ORDERS } from '~/constants/errorCode';
import { KEYWORD_TYPE, MAX_KEYWORD_COUNT, OUTBOUND_STATUS, PERIOD_TYPE } from '~/constants/order';

import ClaimRegisterFirstStepFilter from './ClaimRegisterFirstStepFilter/ClaimRegisterFirstStepFilter';
import ClaimRegisterFirstStepTable from './ClaimRegisterFirstStepTable/ClaimRegisterFirstStepTable';

import type {
  TypeRegisterFirstStepSearchFilterValues,
  TypeRegisterFirstStepSearchFilterValuesState,
} from '~/types/claim.types';
import type { TypeGetOrdersParams, TypeOrders } from '~/types/order.types';

/**
 * [반품 교환 관리 > 반품  & 교환 신청 등록] 첫 번째 단계 컴포넌트
 */
const ClaimRegisterFirstStepContent = ({
  mode,
  setSelectedItem,
}: {
  mode: 'return' | 'exchange';
  setSelectedItem: (order: TypeOrders | null) => void;
}) => {
  const { requestOrderList } = useGetOrders();

  const selectOptions = useClaimStore((state) => state.selectOptions);
  const searchFilterOptions = {
    keyword: selectOptions.orderKeyword,
    dateType: selectOptions.orderDateType,
    deliveryType: [OPTION_ALL, ...selectOptions.deliveryPolicy],
    courier: [OPTION_ALL, ...selectOptions.courierCode],
    clusterCenter: [OPTION_ALL, ...selectOptions.clusterCenter],
  };

  const [
    searchFilterValues,
    searchFilterValuesStatus,
    ordersData,
    setSearchFilterValues,
    setSearchFilterValuesStatus,
    setOrdersData,
  ] = useClaimStore(
    useShallow((state) => [
      state.registerFirstStepSearchFilterValues,
      state.registerFirstStepSearchFilterValuesStatus,
      state.registerFirstStepOrdersData,
      state.actions.setRegisterFirstStepSearchFilterValues,
      state.actions.setRegisterFirstStepSearchFilterValuesStatus,
      state.actions.setRegisterFirstStepOrdersData,
    ])
  );
  const defaultSearchFilterValues = initialClaimStoreValue.registerFirstStepSearchFilterValues;

  const requestOrderListData = (filterValues: TypeRegisterFirstStepSearchFilterValues) => {
    const {
      keywordType,
      keyword,
      dateType,
      deliveryDate,
      paymentStartDateTime,
      paymentEndDateTime,
      deliveryType,
      courier,
      clusterCenter,
      pagination,
    } = filterValues;

    const keywordArr = keyword
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const params: TypeGetOrdersParams = {
      outboundStatus: OUTBOUND_STATUS.COMPLETED.code,
      ...addIf(keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE && keywordArr, 'orderCodes', keywordArr),
      ...addIf(keywordType?.value === KEYWORD_TYPE.CLIENT_ORDER_CODE && keyword, 'clientOrderCodes', keywordArr),
      ...addIf(keywordType?.value === KEYWORD_TYPE.ORDERER_ID && keyword, 'ordererId', keyword),
      ...addIf(keywordType?.value === KEYWORD_TYPE.RECEIVER_NAME && keyword, 'receiverName', keyword),
      ...addIf(
        dateType?.value === PERIOD_TYPE.DELIVERY_DATE && deliveryDate,
        'deliveryStartDate',
        formatDate(deliveryDate?.start, 'YYYY-MM-DD')
      ),
      ...addIf(
        dateType?.value === PERIOD_TYPE.DELIVERY_DATE && deliveryDate,
        'deliveryEndDate',
        formatDate(deliveryDate?.end, 'YYYY-MM-DD')
      ),
      ...addIf(
        dateType?.value === PERIOD_TYPE.PAYMENT_AT && paymentStartDateTime,
        'paymentStartAt',
        `${formatDate(paymentStartDateTime?.date, 'YYYY-MM-DD')} ${paymentStartDateTime?.time}:${
          paymentStartDateTime?.minute
        }:00`
      ),
      ...addIf(
        dateType?.value === PERIOD_TYPE.PAYMENT_AT && paymentEndDateTime,
        'paymentEndAt',
        `${formatDate(paymentEndDateTime?.date, 'YYYY-MM-DD')} ${paymentEndDateTime?.time}:${
          paymentEndDateTime?.minute
        }:59`
      ),
      ...addIf(deliveryType?.value, 'deliveryPolicy'),
      ...addIf(courier?.value, 'courierCode'),
      ...addIf(clusterCenter?.value, 'clusterCenter'),
      offset: pagination.currentPage,
      limit: pagination.pageSize,
    };

    requestOrderList(params, {
      onSuccess: ({ data, pagination }) => {
        setOrdersData(data);

        setSearchFilterValues({
          ...filterValues,
          pagination: {
            ...filterValues.pagination,
            totalCount: pagination.totalCount,
          },
        });
      },
      onError: (error) => {
        const errorCode = error?.code;

        if (errorCode === ORDERS.INVALID_DATE_RANGE) {
          if (dateType?.value === PERIOD_TYPE.DELIVERY_DATE) {
            handleChangeFilterValuesStatus({ deliveryDate: 'error' });
          } else if (dateType?.value === PERIOD_TYPE.PAYMENT_AT) {
            handleChangeFilterValuesStatus({ paymentDateTime: 'error' });
          }

          dialogService.addDialog({
            children: `최대 ${MAX_CLAIM_DATE_RANGE}일 기간으로 입력 후 검색해주세요.`,
          });
          return;
        }

        if (errorCode === ORDERS.INVALID_ORDER_CODES) {
          handleChangeFilterValuesStatus({ keyword: 'error' });

          dialogService.addDialog({
            children: '올바른 형식의 출고요청번호가 아닙니다.\n확인 후 다시 검색해주세요.',
          });
          return;
        }

        toastService.addToast({
          type: 'error',
          message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
        });
      },
    });
  };

  const handleChangeFilterValues = (values: Partial<TypeRegisterFirstStepSearchFilterValues>) => {
    setSearchFilterValues({ ...searchFilterValues, ...values });
  };

  const handleChangeFilterValuesStatus = (status: Partial<TypeRegisterFirstStepSearchFilterValuesState>) => {
    setSearchFilterValuesStatus({ ...searchFilterValuesStatus, ...status });
  };

  const handleSearch = (filterValues: TypeRegisterFirstStepSearchFilterValues = searchFilterValues) => {
    const { keywordType, keyword, dateType, deliveryDate, paymentStartDateTime, paymentEndDateTime } = filterValues;

    /** 출고요청번호, 판매처 주문번호, 배송예정일, 결제완료일시 중 1개 이상 입력 확인 */
    const hasAtLeastOneRequiredValue =
      (keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE && keyword) ||
      (keywordType?.value === KEYWORD_TYPE.CLIENT_ORDER_CODE && keyword) ||
      (dateType?.value === PERIOD_TYPE.DELIVERY_DATE && deliveryDate) ||
      (dateType?.value === PERIOD_TYPE.PAYMENT_AT &&
        paymentStartDateTime?.date &&
        paymentStartDateTime?.time &&
        paymentStartDateTime?.minute &&
        paymentEndDateTime?.date &&
        paymentEndDateTime?.time &&
        paymentEndDateTime?.minute);

    if (!hasAtLeastOneRequiredValue) {
      dialogService.addDialog({
        children: '[출고요청번호, 판매처 주문번호, 배송예정일, 결제완료일시] 중 1개 이상 입력 후 검색해주세요.',
      });
      return;
    }

    /** '출고요청번호'에 알파벳, 숫자 외 다른 문자가 포함된 경우 제한 */
    if (keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE && keyword) {
      const keywordWithOutComma = keyword.replaceAll(',', '');

      if (!validateAlphabetNumericOnly(keywordWithOutComma)) {
        dialogService.addDialog({
          children: '올바른 형식의 출고요청번호가 아닙니다.\n확인 후 다시 검색해주세요.',
        });
        setSearchFilterValuesStatus({ ...searchFilterValuesStatus, keyword: 'error' });
        return;
      }
    }

    /** '키워드' 복수 입력 시, 최대 10개 검색 제한 */
    if (
      keywordType?.value === KEYWORD_TYPE.CLIENT_ORDER_CODE ||
      keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE
    ) {
      if (keyword.split(',').length > MAX_KEYWORD_COUNT) {
        dialogService.addDialog({
          children: `${keywordType?.text}는 최대 ${MAX_KEYWORD_COUNT}개까지 검색할 수 있습니다.`,
        });
        setSearchFilterValuesStatus({ ...searchFilterValuesStatus, keyword: 'error' });
        return;
      }
    }

    /** '결제완료일시' 시작일이 종료일보다 늦은 경우 제한 */
    if (paymentStartDateTime?.date && paymentEndDateTime?.date) {
      const isStartDateLaterThanEndDate = paymentStartDateTime.date > paymentEndDateTime.date;
      const isStartTimeLaterThanEndTime =
        paymentStartDateTime.date.getTime() === paymentEndDateTime.date.getTime() &&
        paymentStartDateTime.time > paymentEndDateTime.time;
      const isStartMinuteLaterThanEndMinute =
        paymentStartDateTime.date.getTime() === paymentEndDateTime.date.getTime() &&
        paymentStartDateTime.time === paymentEndDateTime.time &&
        paymentStartDateTime.minute > paymentEndDateTime.minute;

      if (isStartDateLaterThanEndDate || isStartTimeLaterThanEndTime || isStartMinuteLaterThanEndMinute) {
        dialogService.addDialog({
          children: '정확한 시간을 입력해주세요.',
        });
        setSearchFilterValuesStatus({ ...searchFilterValuesStatus, paymentDateTime: 'error' });
        return;
      }
    }

    requestOrderListData(filterValues);
  };

  const handleResetFilterValues = () => {
    setSearchFilterValues({
      ...defaultSearchFilterValues,
      pagination: searchFilterValues.pagination,
      keywordType: searchFilterOptions.keyword[0] ?? null,
      dateType: searchFilterOptions.dateType[0] ?? null,
    });
    setSearchFilterValuesStatus({
      keyword: 'default',
      deliveryDate: 'default',
      paymentDateTime: 'default',
    });
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: INITIAL_CLAIM_PAGE_CURSOR,
        pageSize: value.value,
      },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({
      ...searchFilterValues,
      pagination: {
        ...searchFilterValues.pagination,
        currentPage: value,
      },
    });
  };

  const handleChangeRowSelection = (selectedRows: IRowNode<TypeOrders>[]) => {
    const selectedItem = selectedRows.map((row) => row.data).find((item) => !!item);
    setSelectedItem(selectedItem ?? null);
  };

  return (
    <ModalContentsContainer gap={8}>
      <Caution
        color={{ contentColor: 'kurly' }}
        contents={[
          {
            children: `${
              mode === 'return' ? '반품' : '교환'
            } 신청은 출고 완료된 주문에 한해 가능하며, 해당 주문만 아래에 표시됩니다.`,
          },
        ]}
      />
      <ClaimRegisterFirstStepFilter
        options={searchFilterOptions}
        values={searchFilterValues}
        valuesStatus={searchFilterValuesStatus}
        onChange={handleChangeFilterValues}
        onChangeStatus={handleChangeFilterValuesStatus}
        onSearch={() =>
          handleSearch({
            ...searchFilterValues,
            pagination: {
              ...searchFilterValues.pagination,
              currentPage: INITIAL_CLAIM_PAGE_CURSOR,
            },
          })
        }
        onReset={handleResetFilterValues}
      />
      <Divider color="gray100" />
      <ClaimRegisterFirstStepTable
        data={ordersData}
        pagination={searchFilterValues.pagination}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onChangeRowSelection={handleChangeRowSelection}
      />
    </ModalContentsContainer>
  );
};

export default ClaimRegisterFirstStepContent;
